import React, { useMemo, useRef, useState, useEffect } from "react";
import { getCurrentAndCompareValue } from "../utils";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Cell,
  LabelList,
} from "recharts";
import { beerPalette } from "../../../../theme/Colours";
import colors from "../../../../theme/Colours";
import {
  StyledWodByProductCard,
  SalesAnalyticsCardLabel,
  SalesAnalyticsContentBox,
  WodSingleChartOverflowBox,
  StyledTooltipContainer,
  StyledTooltipProductText,
  StyledTooltipQuantityText,
  WodTooltipWrapper,
  WodTooltipArrowBorder,
  WodTooltipArrow,
  WodComparisonContainer,
  WodComparisonChartBox,
  WodProductRowBox,
  WodPeriodBarContainer,
  WodProductNameContainer,
  WodBarBox,
  WodBarText,
  WodProductNameText,
  WodZeroBarBox,
  wodAxisStyles,
} from "../styles";

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number }>;
  label?: string | number;
  coordinate?: { x: number; y: number };
}

const CustomTooltip = ({
  active,
  payload,
  coordinate,
  label,
}: CustomTooltipProps) => {
  if (!active || !payload || !coordinate) return null;

  const tooltipWidth = 120;
  const left = coordinate.x - tooltipWidth / 2;

  return (
    <WodTooltipWrapper left={left} top={coordinate.y}>
      <StyledTooltipContainer>
        <StyledTooltipProductText>{label}</StyledTooltipProductText>
        <StyledTooltipQuantityText>
          Percentage: {payload?.[0]?.value}%
        </StyledTooltipQuantityText>
        <WodTooltipArrowBorder />
        <WodTooltipArrow />
      </StyledTooltipContainer>
    </WodTooltipWrapper>
  );
};

interface ProductValue {
  product: string;
  percent: string;
}

interface WodByProductProps {
  comparisonFilterEnabled?: boolean;
  data: {
    key: string;
    name: string;
    details: {
      period: string;
      name: string;
      values: ProductValue[];
    }[];
  };
  periodValues?: { [key: string]: string };
}

export default function WodByProduct({
  comparisonFilterEnabled,
  data,
  periodValues,
}: WodByProductProps) {
  const dataByPeriod = useMemo(() => getCurrentAndCompareValue(data), [data]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Chart Data
  const chartData = dataByPeriod?.current?.values ?? [];
  // Get data for different periods
  const currentData = useMemo(
    () => dataByPeriod?.current?.values ?? [],
    [dataByPeriod]
  );

  const yAxisMax = useMemo(() => {
    const values = currentData.map(
      (item: ProductValue) => Number(item.percent) || 0
    );
    const maxValue = Math.max(...values, 0);

    return Math.ceil(maxValue / 5) * 5 || 5;
  }, [currentData]);

  const yAxisTicks = useMemo(() => {
    const step = yAxisMax / 5;
    return Array.from({ length: 6 }, (_, index) =>
      Number((index * step).toFixed(2))
    );
  }, [yAxisMax]);

  const periodOneData = useMemo(
    () =>
      dataByPeriod?.compare?.find((p: { period: string }) => p.period === "one")
        ?.values ?? [],
    [dataByPeriod]
  );

  const periodTwoData = useMemo(
    () =>
      dataByPeriod?.compare?.find((p: { period: string }) => p.period === "two")
        ?.values ?? [],
    [dataByPeriod]
  );

  useEffect(() => {
    const measureWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };

    measureWidth();
    window.addEventListener("resize", measureWidth);

    return () => window.removeEventListener("resize", measureWidth);
  }, []);

  // Calculate chart width to show exactly 4 products in viewport
  const getChartWidth = () => {
    if (chartData.length <= 4) {
      return "100%";
    }

    if (containerWidth === 0) {
      return chartData.length * 120;
    }

    const widthPer4Products = containerWidth - 40;
    const widthPerProduct = Math.max(120, widthPer4Products / 4);

    return chartData.length * widthPerProduct;
  };

  // Single Chart
  const renderSingleChart = (chartDataParam: ProductValue[]) => (
    <WodSingleChartOverflowBox ref={containerRef} className="wod-product-chart">
      <ResponsiveContainer width={getChartWidth()} height="100%">
        <BarChart
          data={chartDataParam}
          margin={{ top: 20, right: 20, bottom: 35, left: 5 }}
          barCategoryGap="20%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            horizontal={false}
            stroke={colors.wodProductAxisLines}
          />

          <XAxis
            dataKey="product"
            tick={{
              fontSize: "10px",
              fontFamily: "inherit",
              fontWeight: 500,
              fill: colors.black,
              width: 100,
            }}
            style={wodAxisStyles}
            interval={0}
            axisLine={{ stroke: colors.wodProductAxisLines }}
            tickLine={{ stroke: colors.wodProductAxisLines }}
          />

          <YAxis
            ticks={yAxisTicks}
            domain={[0, yAxisMax]}
            axisLine={{
              stroke: colors.wodProductAxisLines,
              strokeDasharray: "3 3",
            }}
            tickLine={{
              stroke: colors.wodProductAxisLines,
              strokeDasharray: "3 3",
            }}
            tick={{ fontSize: "10px", fontFamily: "inherit" }}
            style={wodAxisStyles}
          />

          <Tooltip
            cursor={false}
            offset={20}
            allowEscapeViewBox={{ x: false, y: true }}
            position={{ x: 0, y: 0 }}
            content={(props) => <CustomTooltip {...props} />}
          />

          <Bar
            dataKey="percent"
            barSize={35}
            stroke="none"
            radius={[4, 4, 0, 0]}
          >
            {chartDataParam.map((_entry: ProductValue, index: number) => (
              <Cell
                key={`cell-${index}`}
                fill={beerPalette[index % beerPalette.length]}
              />
            ))}
            <LabelList
              dataKey="percent"
              position="top"
              formatter={(value: React.ReactNode) => `${value ?? 0}%`}
              style={{
                fill: colors.searchInputText,
                fontSize: "12px",
                fontWeight: 500,
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </WodSingleChartOverflowBox>
  );

  // Comparison chart data
  const comparisonChartData = useMemo(() => {
    if (!comparisonFilterEnabled) return [];

    const products = new Set([
      ...periodOneData.map((item: ProductValue) => item.product),
      ...periodTwoData.map((item: ProductValue) => item.product),
    ]);

    return Array.from(products).map((product, idx) => {
      const period1Item = periodOneData.find(
        (item: ProductValue) => item.product === product
      );
      const period2Item = periodTwoData.find(
        (item: ProductValue) => item.product === product
      );

      return {
        product,
        period1: period1Item ? parseFloat(period1Item.percent) : 0,
        period2: period2Item ? parseFloat(period2Item.percent) : 0,
        idx,
      };
    });
  }, [periodOneData, periodTwoData, comparisonFilterEnabled, periodValues]);

  // Comparison chart
  const renderHorizontalComparisonChart = () => (
    <>
      <WodProductRowBox style={{ marginBottom: "10px" }}>
        <WodPeriodBarContainer
          className="left"
          style={{ justifyContent: "center" }}
        >
          <WodProductNameText
            style={{
              fontWeight: 800,
              fontSize: "14px",
              color: colors.salesAnalyticsCardLabelText,
            }}
          >
            {periodValues?.["Period 1"] || "Period 1"}
          </WodProductNameText>
        </WodPeriodBarContainer>
        <WodProductNameContainer />
        <WodPeriodBarContainer
          className="right"
          style={{ justifyContent: "center" }}
        >
          <WodProductNameText
            style={{
              fontWeight: 800,
              fontSize: "14px",
              color: colors.salesAnalyticsCardLabelText,
            }}
          >
            {periodValues?.["Period 2"] || "Period 2"}
          </WodProductNameText>
        </WodPeriodBarContainer>
      </WodProductRowBox>

      <WodComparisonChartBox shouldScroll={comparisonChartData.length > 4}>
        {comparisonChartData.map((row) => (
          <WodProductRowBox key={row.product}>
            {/* Period 1 Bar */}
            <WodPeriodBarContainer className="left">
              {row.period1 === 0 ? (
                <>
                  <WodBarText className="WodBarText">0%</WodBarText>
                  <WodZeroBarBox
                    className="left WodBarBox"
                    productColor={beerPalette[row.idx % beerPalette.length]}
                  />
                </>
              ) : (
                <>
                  <WodBarText className="WodBarText">{row.period1}%</WodBarText>
                  <WodBarBox
                    className="left WodBarBox"
                    sx={{
                      width: `${Math.max(row.period1, 10)}%`,
                      bgcolor: beerPalette[row.idx % beerPalette.length],
                    }}
                  />
                </>
              )}
            </WodPeriodBarContainer>

            {/* Product Name */}
            <WodProductNameContainer>
              <WodProductNameText>{row.product}</WodProductNameText>
            </WodProductNameContainer>

            {/* Period 2 Bar */}
            <WodPeriodBarContainer className="right">
              {row.period2 === 0 ? (
                <>
                  <WodZeroBarBox
                    className="right WodBarBox"
                    productColor={beerPalette[row.idx % beerPalette.length]}
                  />
                  <WodBarText className="WodBarText">0%</WodBarText>
                </>
              ) : (
                <>
                  <WodBarBox
                    className="right WodBarBox"
                    sx={{
                      width: `${Math.max(row.period2, 10)}%`,
                      bgcolor: beerPalette[row.idx % beerPalette.length],
                    }}
                  />
                  <WodBarText className="WodBarText">{row.period2}%</WodBarText>
                </>
              )}
            </WodPeriodBarContainer>
          </WodProductRowBox>
        ))}
      </WodComparisonChartBox>
    </>
  );

  return (
    <StyledWodByProductCard>
      <SalesAnalyticsCardLabel>{data?.name}</SalesAnalyticsCardLabel>

      <SalesAnalyticsContentBox>
        {comparisonFilterEnabled ? (
          <WodComparisonContainer>
            {renderHorizontalComparisonChart()}
          </WodComparisonContainer>
        ) : (
          renderSingleChart(currentData)
        )}
      </SalesAnalyticsContentBox>
    </StyledWodByProductCard>
  );
}
